/* eslint camelcase: ["error", {allow: ["is_locked", "default_max_distance", "default_max_weight", "default_max_volume", "default_time_window", "default_provides"]}] */
import { validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { ContextualMenu } from '../../../../components/menus/ContextualMenu';
import { ConfirmDeleteDialog } from '../../../../dialogs/ConfirmDeleteDialog';
import { Project } from '../../../../domain/project/Project';
import { Supervisor } from '../../../../domain/supervisor/Supervisor';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import { useRetrieveUserToken } from '../../../../hooks/session/useRetrieveUserToken';
import { useDeleteSupervisors } from '../../../../hooks/supervisor/useDeleteSupervisors';
import { useRetrieveUser } from '../../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../../react-components/Text';
import eventsPanel from '../../../../services/events';
import { MixPanelEvents } from '../../../../services/mixpanel';

type SupervisorsContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type SupervisorsActionsMenuType = `contextual` | `popover`;

type SupervisorsActionsMenuProps = {
  type?: SupervisorsActionsMenuType;
  activeProject?: Project;
  supervisors: Supervisor[];
  contextAnchor?: SupervisorsContextAnchor;
  setContextAnchor?: (context?: SupervisorsContextAnchor) => void;
  afterDeleteSupervisors?: (...args: any[]) => any;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
  showViewDetails?: boolean;
  showDeleteSupervisors?: boolean;
};

export const SupervisorsActionsMenu = ({
  type = `contextual` as SupervisorsActionsMenuType,
  supervisors,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  setContextAnchor = () => {},
  afterDeleteSupervisors,
  popoverProps,
  showViewDetails = true,
  showDeleteSupervisors = true,
}: SupervisorsActionsMenuProps) => {
  const intl = useIntl();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { token } = useRetrieveUserToken();
  const { data: activeProject } = useRetrieveActiveProject();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();

  const { run: deleteSupervisors, success } = useDeleteSupervisors();

  const [dialog, setDialog] = useState<`deleteSupervisors` | undefined>(undefined);

  useEffect(() => {
    if (success) {
      setDialog(undefined);
      if (setContextAnchor) {
        setContextAnchor(undefined);
      }

      if (afterDeleteSupervisors) {
        afterDeleteSupervisors(supervisors);
      }
      eventsPanel.supervisorActionsMenu(MixPanelEvents.supervisor.actions_menu.delete);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="routes-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            justifyContent: `space-between`,

            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showViewDetails || supervisors.length !== 1
                ? []
                : [
                    {
                      key: `view_details`,
                      width: 240,
                      onClick: () => {
                        setRightPanelInfo({
                          detailType: `supervisor`,
                          detailId: `${supervisors[0].id}`,
                          detailTab: null,
                        });
                        eventsPanel.supervisorActionsMenu(MixPanelEvents.supervisor.actions_menu.view_details);
                      },
                      content: <Text intlId="supervisors.actions.view_details" style={{ cursor: `pointer` }} />,
                    },
                  ]),
              ...(user &&
              activeProject &&
              supervisors.length === 1 &&
              userCredentials?.projects.test(activeProject.id, `can_manage_supervisors_app`)
                ? [
                    {
                      key: `as_supervisor`,
                      width: 240,
                      disabled: !validatePhoneNumber(supervisors[0].phone ?? ``),
                      onClick: () => {
                        window.open(
                          supervisors[0].getAsSupervisorLink(activeProject.getSupervisorsDomain(), token ?? ``),
                          `_blank`
                        );
                        eventsPanel.supervisorActionsMenu(MixPanelEvents.supervisor.actions_menu.as_supervisor);
                      },
                      content: <Text intlId="supervisors.actions.as_supervisor" style={{ cursor: `pointer` }} />,
                    },
                  ]
                : []),
              ...(!showDeleteSupervisors
                ? []
                : [
                    null,
                    {
                      key: `delete`,
                      width: 240,
                      disabled: supervisors.length === 0,
                      onClick: () => setDialog(`deleteSupervisors`),
                      content: (
                        <Text
                          intlId="supervisors.actions.delete"
                          style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showViewDetails && supervisors.length === 1
                  ? [
                      {
                        key: `view_details`,
                        text: <Text intlId="supervisors.actions.view_details" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          setRightPanelInfo({
                            detailType: `supervisor`,
                            detailId: `${supervisors[0].id}`,
                            detailTab: null,
                          });

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.supervisorActionsMenu(MixPanelEvents.supervisor.actions_menu.view_details);
                        },
                      },
                    ]
                  : []),
                ...(user &&
                activeProject &&
                supervisors.length === 1 &&
                userCredentials?.projects.test(activeProject.id, `can_manage_supervisors_app`)
                  ? [
                      {
                        key: `as_supervisor`,
                        disabled: !validatePhoneNumber(supervisors[0].phone ?? ``),
                        text: <Text intlId="supervisors.actions.as_supervisor" style={{ cursor: `pointer` }} />,
                        onClick: () => {
                          window.open(
                            supervisors[0].getAsSupervisorLink(activeProject.getSupervisorsDomain(), token ?? ``),
                            `_blank`
                          );

                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          eventsPanel.supervisorActionsMenu(MixPanelEvents.supervisor.actions_menu.as_supervisor);
                        },
                      },
                    ]
                  : []),
                ...(showDeleteSupervisors
                  ? [
                      null,
                      {
                        key: `delete`,
                        disabled: supervisors.length === 0,
                        text: (
                          <Text
                            intlId="supervisors.actions.delete"
                            style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`deleteSupervisors`);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      )}

      <ConfirmDeleteDialog
        typeData="supervisors"
        open={dialog === `deleteSupervisors`}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={supervisors}
        confirmInputValue={intl.formatMessage({ id: `confirm` })}
        handleDelete={() => {
          deleteSupervisors({ supervisorIds: supervisors.map((cli: any) => cli.id) });
        }}
      />
    </>
  );
};
